/**
 * Animator class for managing 60fps animation intervals with time accumulation.
 * Used for hourly playback controls in the dataset module.
 */
export class Animator {
  constructor() {
    this._intervalId = null
    this.isRunning = false
    this._callback = null
  }

  /**
   * Internal method to set interval ID and update running state.
   * Clears any existing interval before setting a new one.
   *
   * @private
   * @param {number|null} intervalId - The interval ID from setInterval, or null to clear
   */
  _setIntervalId(intervalId) {
    if (this._intervalId != null) {
      clearInterval(this._intervalId)
    }

    this._intervalId = intervalId
    this.isRunning = intervalId != null
  }

  /**
   * Start the 60fps animation loop.
   * Clears any existing interval before starting a new one.
   * Calls the registered callback with accumulated time delta in seconds.
   */
  start() {
    this._setIntervalId(null)

    const this2 = this
    let current = new Date()
    let offset = 0

    const handler = () => {
      const now = new Date()
      const delta = (now - current) / 1000
      if (this2._callback && this2._callback(delta + offset)) {
        offset = 0
      } else {
        offset += delta
      }

      current = now
    }

    this._setIntervalId(setInterval(handler, 1000 / 60))
  }

  /**
   * Stop the animation loop.
   * Clears the interval and sets isRunning to false.
   */
  stop() {
    this._setIntervalId(null)
  }

  /**
   * Set the callback function to be called on each animation frame.
   * Callback receives accumulated time in seconds and should return:
   * - true: to reset time accumulation
   * - false: to continue accumulating time
   *
   * @param {Function} callback - Function(accumulatedSeconds) => boolean
   */
  setCallback(callback) {
    this._callback = callback
  }

  /**
   * Cleanup method to stop animation and clear callback.
   * Should be called when the animator is no longer needed.
   */
  destroy() {
    this._setIntervalId(null)
    this._callback = null
  }
}
